import { Button, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import type { PaginatedQueryResult } from "@/shared/hooks/usePaginatedQuery";
import type { ScheduledTask } from "../types";
import { HOLAT_RANGLARI, qotibQolgan, retryTaqiqi } from "../utils/tasks";

interface ScheduledTasksTableProps {
	query: PaginatedQueryResult<ScheduledTask>;
	canRetry: boolean;
	retryingId: string | null;
	onRetry: (task: ScheduledTask) => void;
}

/**
 * `scheduled_tasks` ro'yxati.
 *
 * ⚠️⚠️ **`processing` vazifada «Qayta urinish» O'CHIRILGAN** va tooltip
 * sababini tushuntiradi. Worker ayni paytda uni bajarayotgan bo'lishi
 * mumkin; `pending` ga qaytarish IKKINCHI bajaruvchini yaratardi.
 * Backend ham 422 beradi — tugmani o'chirish shu javobni oldindan
 * ko'rsatadi, uni takrorlamaydi.
 *
 * ⚠️ Qotib qolgan (`locked_at` eskirgan) vazifa uchun alohida izoh:
 * `runSchedulerTick` uni O'ZI qayta oladi, qo'lda aralashish kerak emas.
 */
export function ScheduledTasksTable({
	query,
	canRetry,
	retryingId,
	onRetry,
}: ScheduledTasksTableProps) {
	const { t } = useTranslation("jobs");

	const columns: ColumnsType<ScheduledTask> = [
		{ title: t("scheduledTasks.columns.kind"), dataIndex: "kind", key: "kind" },
		{
			title: t("scheduledTasks.columns.subject"),
			key: "subject",
			render: (_, row) => `${row.subjectType} · ${row.subjectId.slice(0, 8)}`,
		},
		{
			title: t("scheduledTasks.columns.due"),
			key: "dueAt",
			render: (_, row) => dayjs(row.dueAt).format("DD.MM.YYYY HH:mm"),
		},
		{
			title: t("scheduledTasks.columns.status"),
			key: "status",
			render: (_, row) => (
				<Space orientation="vertical" size={2}>
					<Tag color={HOLAT_RANGLARI[row.status]}>{t(`scheduledTasks.status.${row.status}`)}</Tag>
					{qotibQolgan(row) ? (
						<Typography.Text type="secondary" className="text-xs">
							{t("scheduledTasks.stuckHint")}
						</Typography.Text>
					) : null}
				</Space>
			),
		},
		{
			title: t("scheduledTasks.columns.attempts"),
			dataIndex: "attempts",
			key: "attempts",
			width: 110,
		},
		{
			title: t("scheduledTasks.columns.lastError"),
			key: "lastError",
			render: (_, row) => (
				<Typography.Paragraph className="!mb-0" ellipsis={{ rows: 2, expandable: true }}>
					{row.lastError ?? "—"}
				</Typography.Paragraph>
			),
		},
	];

	if (canRetry) {
		columns.push({
			title: "",
			key: "actions",
			render: (_, row) => {
				const taqiq = retryTaqiqi(row);

				return (
					<Tooltip title={taqiq ? t("scheduledTasks.retryBlockedProcessing") : ""}>
						<Button
							type="link"
							disabled={taqiq !== null}
							loading={retryingId === row.id}
							onClick={() => onRetry(row)}
						>
							{t("scheduledTasks.retry")}
						</Button>
					</Tooltip>
				);
			},
		});
	}

	return <DataTable<ScheduledTask> columns={columns} query={query} />;
}
